import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  MagnifyingGlass, 
  Trash, 
  CaretDown, 
  CaretUp, 
  Phone, 
  WhatsappLogo, 
  Calendar,
  PencilSimple,
  Check,
  X,
  FileText
} from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { maskPhone, canViewFullPhone } from '../../utils/phoneMask';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CallingAgentReports({ onBack }) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const hasFullPhoneAccess = canViewFullPhone(user);

  const [search, setSearch] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Editing Remark State
  const [editingId, setEditingId] = useState(null);
  const [editingNote, setEditingNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Fetch calling activity reports
  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (selectedDate) params.append('date', selectedDate);

      const response = await axios.get(`${API}/calling-reports?${params.toString()}`);
      const data = response.data || [];
      setActivities(data);

      // Keep groups closed by default
      setExpandedGroups({});
    } catch (error) {
      console.error('Failed to load calling reports:', error);
      toast.error('Failed to load calling agent reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [search, selectedDate]);

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const handleStartEditNote = (activity) => {
    setEditingId(activity.id);
    setEditingNote(activity.note || 'Ok done');
  };

  const handleSaveNote = async (activityId) => {
    if (!editingNote || !editingNote.trim()) {
      toast.error('Remark cannot be empty');
      return;
    }
    setSavingNote(true);
    try {
      await axios.patch(`${API}/calling-reports/${activityId}/remark`, { note: editingNote.trim() });
      toast.success('Remark updated successfully');
      setEditingId(null);
      fetchReports();
    } catch (error) {
      console.error('Failed to update remark:', error);
      toast.error(error.response?.data?.error || 'Failed to update remark');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm('Are you sure you want to delete this activity record?')) return;
    try {
      await axios.delete(`${API}/calling-reports/${activityId}`);
      toast.success('Record deleted');
      fetchReports();
    } catch (error) {
      console.error('Failed to delete report:', error);
      toast.error(error.response?.data?.error || 'Failed to delete record');
    }
  };

  // Group activities by Handler Name + Date
  const groupedActivities = activities.reduce((acc, activity) => {
    const d = activity.created_at ? new Date(activity.created_at) : new Date();
    const dateFormatted = d.toISOString().split('T')[0];
    const groupKey = `${activity.handler_name || 'Handler'}_${dateFormatted}`;

    if (!acc[groupKey]) {
      acc[groupKey] = {
        handlerName: activity.handler_name || 'Handler',
        dateStr: dateFormatted,
        formattedDateStr: d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        items: []
      };
    }
    acc[groupKey].items.push(activity);
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full bg-[#f8fafc] text-gray-800 text-xs overflow-hidden">
      
      {/* ─── Top Header Bar (Green Theme) ─────────────────────────────────────── */}
      <div className="bg-[#10b981] text-white px-5 py-3.5 flex items-center justify-between shadow-md flex-shrink-0">
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors focus:outline-none"
              title="Back"
            >
              <ArrowLeft size={18} weight="bold" />
            </button>
          )}
          <div>
            <h1 className="text-base font-bold tracking-wide">Calling Agent Reports</h1>
            <p className="text-[11px] text-emerald-100 font-medium">Admin Control Center</p>
          </div>
        </div>
      </div>

      {/* ─── Body Content ────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 max-w-5xl w-full mx-auto">
        
        {/* Info Banner Box */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex items-start gap-3">
          <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg flex-shrink-0 mt-0.5">
            <FileText size={20} weight="bold" />
          </div>
          <div>
            <h3 className="font-bold text-sm text-gray-900 mb-0.5">Calling Agent Reports</h3>
            <p className="text-xs text-gray-500 leading-relaxed">
              Every daily/weekly/custom activity report sent by a Calling Handler. Sent reports are permanent here unless deleted with your admin password.
            </p>
          </div>
        </div>

        {/* Filter Bar (Search & Date) */}
        <div className="flex flex-col sm:flex-row items-center gap-3">
          {/* Search by Handler */}
          <div className="relative flex-1 w-full">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search by handler name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg bg-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all placeholder-gray-400"
            />
          </div>

          {/* Date Picker */}
          <div className="relative w-full sm:w-auto">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-200 rounded-lg bg-white text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all text-gray-700"
            />
          </div>
        </div>

        {/* Reports Content List */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
            <p className="font-medium animate-pulse">Loading reports...</p>
          </div>
        ) : Object.keys(groupedActivities).length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 space-y-2">
            <Phone size={36} className="mx-auto text-gray-300" weight="light" />
            <p className="font-bold text-sm text-gray-700">No calling agent reports found</p>
            <p className="text-xs text-gray-400">Activities will appear here when handlers click Call or WhatsApp</p>
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(groupedActivities).map(([groupKey, group]) => {
              const isExpanded = !!expandedGroups[groupKey];
              return (
                <div key={groupKey} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm transition-all">
                  
                  {/* Accordion Group Header */}
                  <div 
                    onClick={() => toggleGroup(groupKey)}
                    className={`px-4 py-3 bg-gray-50/70 hover:bg-gray-100/70 flex items-center justify-between cursor-pointer select-none transition-colors ${isExpanded ? 'border-b border-gray-200' : ''}`}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900 text-sm">{group.handlerName}</span>
                        <span className="text-xs text-gray-400 font-mono">{group.dateStr}</span>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        Sent {group.formattedDateStr} · {group.items.length} {group.items.length === 1 ? 'activity' : 'activities'}
                      </p>
                    </div>

                    <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                      {isSuperAdmin && (
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete all records for ${group.handlerName} on ${group.dateStr}?`)) {
                              group.items.forEach(item => handleDeleteActivity(item.id));
                            }
                          }}
                          className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Delete group records"
                        >
                          <Trash size={16} />
                        </button>
                      )}
                      <button 
                        onClick={() => toggleGroup(groupKey)}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                      >
                        {isExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
                      </button>
                    </div>
                  </div>

                  {/* Accordion Group Table */}
                  {isExpanded && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse text-[11px]">
                        <thead className="bg-gray-100/50 text-gray-500 font-bold uppercase tracking-wider border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-2.5">KIND</th>
                            <th className="px-4 py-2.5">CLIENT</th>
                            <th className="px-4 py-2.5">PHONE</th>
                            <th className="px-4 py-2.5">MB</th>
                            <th className="px-4 py-2.5">SERIAL / CLIENT ID</th>
                            <th className="px-4 py-2.5">TIME</th>
                            <th className="px-4 py-2.5">NOTE</th>
                            {isSuperAdmin && <th className="px-4 py-2.5 text-right">ACTION</th>}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {group.items.map((item) => {
                            const isWhatsApp = item.kind === 'whatsapp';
                            const formattedTime = item.created_at 
                              ? new Date(item.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                              : '-';

                            return (
                              <tr key={item.id} className="hover:bg-gray-50/80 transition-colors">
                                
                                {/* KIND */}
                                <td className="px-4 py-2.5 whitespace-nowrap font-bold">
                                  {isWhatsApp ? (
                                    <div className="flex items-center gap-1.5 text-emerald-600">
                                      <WhatsappLogo size={16} weight="fill" />
                                      <span>WhatsApp</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1.5 text-blue-600">
                                      <Phone size={16} weight="fill" />
                                      <span>Call</span>
                                    </div>
                                  )}
                                </td>

                                {/* CLIENT */}
                                <td className="px-4 py-2.5 font-medium text-gray-900 whitespace-nowrap">
                                  {item.customer_name || 'Client'}
                                </td>

                                {/* PHONE */}
                                <td className="px-4 py-2.5 font-mono text-gray-600 whitespace-nowrap">
                                  {maskPhone(item.phone, hasFullPhoneAccess) || '-'}
                                </td>

                                {/* MB */}
                                <td className="px-4 py-2.5 whitespace-nowrap">
                                  <span className="bg-gray-100 text-gray-700 border border-gray-200 px-2 py-0.5 rounded font-semibold text-[10px]">
                                    {item.mb_setup || 'ALL MB'}
                                  </span>
                                </td>

                                {/* SERIAL / CLIENT ID */}
                                <td className="px-4 py-2.5 font-mono text-gray-600 whitespace-nowrap">
                                  {item.serial_id || '01'}
                                </td>

                                {/* TIME */}
                                <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                                  {formattedTime}
                                </td>

                                {/* NOTE / Remark (Editable) */}
                                <td className="px-4 py-2.5 text-gray-700">
                                  {editingId === item.id ? (
                                    <div className="flex items-center gap-1.5 min-w-[180px]">
                                      <input
                                        type="text"
                                        value={editingNote}
                                        onChange={(e) => setEditingNote(e.target.value)}
                                        className="px-2 py-1 border border-emerald-400 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 flex-1 bg-white"
                                        autoFocus
                                      />
                                      <button
                                        onClick={() => handleSaveNote(item.id)}
                                        disabled={savingNote}
                                        className="p-1 text-white bg-emerald-600 hover:bg-emerald-700 rounded transition-colors"
                                        title="Save"
                                      >
                                        <Check size={14} weight="bold" />
                                      </button>
                                      <button
                                        onClick={() => setEditingId(null)}
                                        className="p-1 text-gray-500 hover:bg-gray-200 rounded transition-colors"
                                        title="Cancel"
                                      >
                                        <X size={14} weight="bold" />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 group">
                                      <span>{item.note || 'Ok done'}</span>
                                      {(isSuperAdmin || String(user?.id) === String(item.handler_id) || user?.username === item.handler_name) && (
                                        <button
                                          onClick={() => handleStartEditNote(item)}
                                          className="text-gray-400 hover:text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
                                          title="Edit remark"
                                        >
                                          <PencilSimple size={14} />
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </td>

                                {/* ACTION (Delete) */}
                                {isSuperAdmin && (
                                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                    <button
                                      onClick={() => handleDeleteActivity(item.id)}
                                      className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                      title="Delete activity record"
                                    >
                                      <Trash size={15} />
                                    </button>
                                  </td>
                                )}

                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}
